import { useEffect, useMemo, useState } from "react";
import { get, useFormContext } from "react-hook-form";
import {
  type PasswordStrength,
  calculatePasswordStrength,
  isGreatPassword,
} from "@/react/helpers/passwordStrength";
import { PASSWORD_REGEX } from "@circle-react/components/OnboardingPage/helpers/constants";
import { CheckoutForm } from "@circle-react/components/Paywalls/Checkout/CheckoutV2/CheckoutForm";
import { CheckoutFormField } from "../../../CheckoutFormField";
import { CheckoutFormPasswordStrengthIndicator } from "./CheckoutFormPasswordStrengthIndicator";
import { CheckoutFormPasswordVisibilityToggle } from "./CheckoutFormPasswordVisibilityToggle";

export const CheckoutFormPasswordField = () => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const {
    watch,
    formState: { errors },
    trigger,
  } = useFormContext();

  const passwordValue = watch("password");
  const passwordFieldError = get(errors, "password");

  const passwordStrength = useMemo<PasswordStrength>(
    () => calculatePasswordStrength(passwordValue || ""),
    [passwordValue],
  );

  useEffect(() => {
    if (!passwordValue) setIsPasswordVisible(false);
  }, [passwordValue]);

  useEffect(() => {
    if (isGreatPassword(passwordStrength) && passwordFieldError) {
      void trigger("password");
    }
  }, [passwordStrength, passwordFieldError, trigger]);

  const handleBlur = async () => {
    await trigger("password");
  };

  return (
    <CheckoutForm.Item name="password">
      <div className="relative flex items-center">
        <CheckoutFormField
          type={isPasswordVisible && passwordValue ? "text" : "password"}
          name="password"
          options={{
            required: true,
            pattern: PASSWORD_REGEX,
          }}
          className="w-full"
          onBlur={handleBlur}
        />
        <CheckoutFormPasswordVisibilityToggle
          passwordValue={passwordValue}
          isPasswordVisible={isPasswordVisible}
          setIsPasswordVisible={setIsPasswordVisible}
        />
      </div>
      <CheckoutFormPasswordStrengthIndicator
        passwordStrength={passwordStrength}
        passwordFieldError={passwordFieldError}
        passwordValue={passwordValue}
      />
    </CheckoutForm.Item>
  );
};
